import { getFormattedCouponReduction } from "@/modules/common/couponObject/utils/couponObjectUtils"
import { WARNING_SESSION_COUNT } from "@modules/boxConsumable/entities/boxConsumableConst"
import { BoxConsumableStatus } from "@modules/boxConsumable/entities/boxConsumableType"
import { type BoxConsumableDetailsDto } from "@modules/boxConsumable/gateway/boxConsumableApiDto"
import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { Language } from "@modules/language/utils/languageUtils"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"

type BoxConsumableDetailsState = Omit<
  BoxConsumableDetailsEntity,
  | "getWordingStatus"
  | "getColorStatus"
  | "getWordingCouponDate"
  | "getAppliedPromocodeWording"
  | "getAppliedCouponWording"
  | "isDisplayWarning"
  | "getWordingWarning"
  | "getCurrentPrice"
>
export class BoxConsumableDetailsEntity {
  id: string
  type: BoxOfferType
  creationDate: Date
  deferredDate: Date | null
  endValidity: Date | null
  status: BoxConsumableStatus
  count: number | null
  referencePrice: PriceObjectEntity | null
  priceObject: PriceObjectEntity | null
  usedPromoCode: LightPromoCodeEntity | null
  coupon: CouponEntity | null
  offer: BoxOfferDetailedEntity

  private constructor(boxConsumableDetails: BoxConsumableDetailsState) {
    this.id = boxConsumableDetails.id
    this.type = boxConsumableDetails.type
    this.creationDate = boxConsumableDetails.creationDate
    this.deferredDate = boxConsumableDetails.deferredDate
    this.endValidity = boxConsumableDetails.endValidity
    this.status = boxConsumableDetails.status
    this.count = boxConsumableDetails.count
    this.referencePrice = boxConsumableDetails.referencePrice
    this.priceObject = boxConsumableDetails.priceObject
    this.usedPromoCode = boxConsumableDetails.usedPromoCode
    this.coupon = boxConsumableDetails.coupon
    this.offer = boxConsumableDetails.offer
  }

  static fromDto(boxConsumableDetailsDto: BoxConsumableDetailsDto): BoxConsumableDetailsEntity {
    return new BoxConsumableDetailsEntity({
      id: boxConsumableDetailsDto.id,
      type: boxConsumableDetailsDto.type,
      creationDate: new Date(boxConsumableDetailsDto.creationDate),
      deferredDate: boxConsumableDetailsDto.deferredDate ? new Date(boxConsumableDetailsDto.deferredDate) : null,
      endValidity: boxConsumableDetailsDto.endValidity ? new Date(boxConsumableDetailsDto.endValidity) : null,
      status: boxConsumableDetailsDto.status,
      count: boxConsumableDetailsDto.count,
      referencePrice: boxConsumableDetailsDto.referencePrice
        ? PriceObjectEntity.fromDto(boxConsumableDetailsDto.referencePrice)
        : null,
      priceObject: boxConsumableDetailsDto.priceObject
        ? PriceObjectEntity.fromDto(boxConsumableDetailsDto.priceObject)
        : null,
      usedPromoCode: boxConsumableDetailsDto.usedPromoCode
        ? LightPromoCodeEntity.fromDto(boxConsumableDetailsDto.usedPromoCode)
        : null,
      coupon: boxConsumableDetailsDto.coupon ? CouponEntity.fromDto(boxConsumableDetailsDto.coupon) : null,
      offer: BoxOfferDetailedEntity.fromDto(boxConsumableDetailsDto.offer),
    })
  }

  getWordingStatus(): string {
    switch (this.status) {
      case BoxConsumableStatus.PAID:
        if (this.count === 0 && this.offer.type === BoxOfferType.TicketsBook)
          return word("box.members.tab.offer.status.expiredPass")
        else if (this.count === 0) return word("box.members.tab.offer.status.expired")
        return word("box.members.tab.offer.status.ongoing")
      case BoxConsumableStatus.PENDING_PAYMENT:
        return ""
      default:
        return ""
    }
  }

  getColorStatus(): string {
    switch (this.status) {
      case BoxConsumableStatus.PAID:
        if (this.count === 0) return "text-red-500"
        return "text-textSuccess"
      case BoxConsumableStatus.PENDING_PAYMENT:
        return "text-custom-yellow-500"
      default:
        return "text-red-500"
    }
  }

  getWordingCouponDate(): string {
    return ""
  }

  getAppliedPromocodeWording(language: Language): string {
    if (!this.referencePrice || !this.usedPromoCode) {
      return ""
    }

    const initialPrice = this.referencePrice.value
    const reduction =
      this.usedPromoCode.type === CouponType.PERCENT
        ? (initialPrice * this.usedPromoCode.value) / 100
        : this.usedPromoCode.value
    const resultingPrice = initialPrice - reduction
    const formattedReduction = getFormattedCouponReduction(
      this.usedPromoCode.type,
      this.usedPromoCode.value,
      language,
      this.referencePrice.currency,
    )

    if (this.usedPromoCode.type === CouponType.PERCENT) {
      return (
        `${word("promoCode.short", { code: this.usedPromoCode.code })}: ` +
        `${word("coupon.subscription.reduction.definition", {
          percent: formattedReduction,
          price: formatCurrency(initialPrice, this.referencePrice.currency, language),
        })} ` +
        `= -${formatCurrency(reduction, this.referencePrice.currency, language)} ` +
        `${word("coupon.subscription.reduction.result", {
          result: formatCurrency(resultingPrice, this.referencePrice.currency, language),
        })}`
      )
    } else {
      return (
        `${word("promoCode.short", { code: this.usedPromoCode.code })}: ` +
        `-${formattedReduction}, ${word("coupon.subscription.reduction.result", {
          result: formatCurrency(resultingPrice, this.referencePrice.currency, language),
        })}`
      )
    }
  }

  // The coupon is calculated after calculating the promo code if there is one
  getAppliedCouponWording(language: Language): string {
    if (!this.priceObject || !this.referencePrice || !this.coupon) {
      return ""
    }

    let initialPrice = this.referencePrice.value
    if (this.usedPromoCode !== null) {
      initialPrice =
        this.usedPromoCode.type === CouponType.PERCENT
          ? initialPrice - (initialPrice * this.usedPromoCode.value) / 100
          : initialPrice - this.usedPromoCode.value
    }

    const reduction =
      this.coupon.type === CouponType.PERCENT ? (initialPrice * this.coupon.value) / 100 : this.coupon.value
    const resultingPrice = initialPrice - reduction

    const formattedReduction = getFormattedCouponReduction(
      this.coupon.type,
      this.coupon.value,
      language,
      this.priceObject.currency,
    )

    if (this.coupon.type === CouponType.PERCENT) {
      return (
        `${word("coupon.subscription.reduction.definition", {
          percent: formattedReduction,
          price: formatCurrency(initialPrice, this.referencePrice.currency, language),
        })} ` +
        `= -${formatCurrency(reduction, this.priceObject.currency, language)} ` +
        ` ${word("coupon.subscription.reduction.result", { result: formatCurrency(resultingPrice, this.priceObject.currency, language) })}`
      )
    } else {
      return `-${formattedReduction}, ${word("coupon.subscription.reduction.result", {
        result: formatCurrency(resultingPrice, this.priceObject.currency, language),
      })}`
    }
  }

  isDisplayWarning(): boolean {
    if (
      this.status === BoxConsumableStatus.PAID &&
      this.offer.type === BoxOfferType.TicketsBook &&
      this.count !== null &&
      this.count > 0 &&
      this.count <= WARNING_SESSION_COUNT
    ) {
      return true
    }
    return false
  }

  getWordingWarning(): string {
    if (this.count === null) return ""
    return word("box.purchases.list.item.endCount", {
      count: this.count ?? 0,
    })
  }

  getCurrentPrice(): PriceObjectEntity | null {
    if (!this.referencePrice?.value) {
      return null
    }

    let price = this.referencePrice.value

    if (this.usedPromoCode) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }
    if (this.coupon) {
      price =
        this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value
    }

    return PriceObjectEntity.fromValue(price, this.referencePrice.currency)
  }
}
